import { useState, useEffect } from 'react';

/**
 * Custom hook for responsive media queries
 * @param {string} query - CSS media query string
 * @returns {boolean} - Whether the media query matches
 */
export function useMediaQuery(query) {
  // Read the query synchronously so the first paint already uses the right
  // layout (no one-frame flash of the narrow/wide variant).
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? Boolean(window.matchMedia(query)?.matches)
      : false
  ));

  useEffect(() => {
    const media = typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;
    if (!media) return undefined;
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener?.('change', listener);
    return () => media.removeEventListener?.('change', listener);
  }, [matches, query]);

  return matches;
}

/**
 * Hook to detect mobile viewport (< 768px)
 * @returns {boolean} - True if mobile viewport
 */
export function useIsMobile() {
  return !useMediaQuery('(min-width: 768px)');
}

/**
 * Hook to detect desktop viewport (>= 768px)
 * @returns {boolean} - True if desktop viewport
 */
export function useIsDesktop() {
  return useMediaQuery('(min-width: 768px)');
}

export default useMediaQuery;
